import type { FormEvent } from "react";
import {
  ArrowRight,
  Check,
  CircleDollarSign,
  MapPin,
  PackageCheck,
  ShieldCheck,
  TicketCheck,
  UserRound,
} from "lucide-react";
import { AccountShell } from "../../account";
import { OrganizationApplicationSection } from "../../organization-application";
import {
  openOrganizationBilling,
  postOrganizationAction,
  refundOrganizationOrder,
  updateOrganization,
} from "../api";
import { OrganizationWorkspaceShell } from "../components/OrganizationWorkspaceShell";
import { useOrganizationWorkspace } from "../hooks/useOrganizationWorkspace";
import { goalDefinitions, stepStatusLabel } from "../model";
import { OrganizationAreasPage } from "./OrganizationAreasPage";
import { OrganizationFinancePage } from "./OrganizationFinancePage";
import { OrganizationPackagePage } from "./OrganizationPackagePage";
import { OrganizationPermitsPage } from "./OrganizationPermitsPage";
import { OrganizationSettingsPage } from "./OrganizationSettingsPage";
import { OrganizationTeamPage } from "./OrganizationTeamPage";

export function OrganizationWorkspacePage({ slug }: { slug: string }) {
  const {
    areas,
    data,
    error,
    fail,
    finish,
    message,
    paymentBusy,
    products,
    selectedProduct,
    setError,
    setMessage,
    setPaymentBusy,
    setSelectedProduct,
    setTab,
    tab,
  } = useOrganizationWorkspace(slug);
  const submitGoals = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget).getAll("goal").map(String);
    const capabilities = goalDefinitions
      .filter((goal) => values.includes(goal.id))
      .flatMap((goal) => [...goal.capabilities]);
    try {
      await postOrganizationAction(slug, "capabilities", { capabilities });
      finish(
        "Käyttötarkoitus tallennettiin ja tarvittavat toiminnot otettiin käyttöön.",
      );
    } catch (cause) {
      fail(cause, "Käyttötarkoitusta ei voitu tallentaa.");
    }
  };
  const proposeArea = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      values = new FormData(form);
    try {
      await postOrganizationAction(slug, "area-roles", {
        coreAreaId: values.get("coreAreaId"),
        relationship: values.get("relationship"),
      });
      form.reset();
      finish("Aluekytkentä lähetettiin Platformin varmennettavaksi.");
    } catch (cause) {
      fail(cause, "Aluekytkentää ei voitu tallentaa.");
    }
  };
  const linkProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      values = new FormData(form);
    try {
      await postOrganizationAction(slug, "permit-products", {
        permitProductId: values.get("permitProductId"),
        relationship: values.get("relationship"),
      });
      form.reset();
      setSelectedProduct("");
      finish("Lupatuotekytkentä lähetettiin Platformin varmennettavaksi.");
    } catch (cause) {
      fail(cause, "Lupatuotetta ei voitu liittää.");
    }
  };
  const proposePermitProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      values = new FormData(form),
      price = String(values.get("proposedPrice") ?? "").trim(),
      quota = String(values.get("quotaLimit") ?? "").trim();
    try {
      await postOrganizationAction(slug, "permit-product-applications", {
        coreAreaId: values.get("coreAreaId"),
        name: values.get("name"),
        activity: values.get("activity"),
        methods: [values.get("method")],
        validFrom: values.get("validFrom"),
        validTo: values.get("validTo"),
        quotaLimit: quota ? Number(quota) : null,
        quotaPeriod: quota ? values.get("quotaPeriod") : null,
        sourceUrl: values.get("sourceUrl"),
        decisionReference: values.get("decisionReference"),
        proposedUnitAmount: price ? Math.round(Number(price) * 100) : null,
      });
      form.reset();
      finish("Uusi lupatuotehakemus lähetettiin Platformin tarkastettavaksi.");
    } catch (cause) {
      fail(cause, "Lupatuotehakemusta ei voitu lähettää.");
    }
  };
  const invite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      values = new FormData(form);
    try {
      await postOrganizationAction(slug, "invitations", {
        email: values.get("email"),
        role: values.get("role"),
      });
      form.reset();
      finish("Kutsu lisättiin organisaatiotilin kutsuihin.");
    } catch (cause) {
      fail(cause, "Kutsua ei voitu lisätä.");
    }
  };
  const payment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (paymentBusy) return;
    const values = new FormData(event.currentTarget);
    setPaymentBusy(true);
    setMessage("");
    setError("");
    try {
      const result = await postOrganizationAction<{ onboardingUrl?: string }>(
        slug,
        "payment-account",
        { provider: values.get("provider") },
      );
      if (result.onboardingUrl) {
        window.location.assign(result.onboardingUrl);
        return;
      }
      finish("Stripe Connect -maksutilin käyttöönotto aloitettiin.");
    } catch (cause) {
      fail(cause, "Maksutiliä ei voitu valmistella.");
    } finally {
      setPaymentBusy(false);
    }
  };
  const offer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      values = new FormData(form);
    try {
      await postOrganizationAction(slug, "offers", {
        permitProductId: values.get("permitProductId"),
        issuerOrganizationId: values.get("issuerOrganizationId"),
        displayName: values.get("displayName"),
        unitAmount: Math.round(Number(values.get("price")) * 100),
        platformFeeAmount: Math.round(Number(values.get("platformFee")) * 100),
        termsUrl: values.get("termsUrl"),
      });
      form.reset();
      setSelectedProduct("");
      finish("Myyntitarjous tallennettiin luonnoksena.");
    } catch (cause) {
      fail(cause, "Tarjousta ei voitu tallentaa.");
    }
  };
  const refundOrder = async (orderId: string, orderNumber: string) => {
    if (
      !window.confirm(
        `Palautetaanko tilauksen ${orderNumber} koko jäljellä oleva summa? Lupaoikeus perutaan vasta Stripen vahvistuksen jälkeen.`,
      )
    )
      return;
    try {
      await refundOrganizationOrder(
        slug,
        orderId,
        "Organisaation hyväksymä täysi palautus",
      );
      finish("Palautus lähetettiin Stripelle käsiteltäväksi.");
    } catch (cause) {
      fail(cause, "Palautusta ei voitu käynnistää.");
    }
  };
  const requestPlanChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      values = new FormData(form);
    try {
      await postOrganizationAction(slug, "service-plan-change", {
        servicePlanId: values.get("servicePlanId"),
        note: values.get("note"),
      });
      form.reset();
      finish(
        "Paketin vaihtopyyntö lähetettiin Platformin käsiteltäväksi. Mitään ei veloitettu.",
      );
    } catch (cause) {
      fail(cause, "Paketin vaihtopyyntöä ei voitu lähettää.");
    }
  };
  const openBillingCheckout = async () => {
    try {
      const result = await openOrganizationBilling(slug, "checkout");
      window.location.assign(result.url);
    } catch (cause) {
      fail(cause, "Laskutuskassaa ei voitu avata.");
    }
  };
  const openBillingPortal = async () => {
    try {
      const result = await openOrganizationBilling(slug, "portal");
      window.location.assign(result.url);
    } catch (cause) {
      fail(cause, "Laskutuksen itsepalvelua ei voitu avata.");
    }
  };
  const settings = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    try {
      const result = await updateOrganization<{ requiresVerification: boolean }>(
        slug,
        {
          name: values.get("name"),
          businessId: values.get("businessId"),
          contactEmail: values.get("contactEmail"),
        },
      );
      finish(
        result.requiresVerification
          ? "Perustiedot tallennettiin. Nimen tai Y-tunnuksen muutos odottaa uutta varmennusta."
          : "Yhteystiedot tallennettiin.",
      );
    } catch (cause) {
      fail(cause, "Asetuksia ei voitu tallentaa.");
    }
  };

  if (error)
    return (
      <AccountShell signedIn accountType="organization">
        <main className="account-main">
          <div className="notice error">{error}</div>
        </main>
      </AccountShell>
    );
  if (!data)
    return (
      <AccountShell signedIn accountType="organization">
        <main className="account-main">
          <div className="account-loading">
            Ladataan organisaation työtilaa…
          </div>
        </main>
      </AccountShell>
    );
  const canAdmin = ["owner", "admin"].includes(data.organization.role),
    canEdit = ["owner", "admin", "editor"].includes(data.organization.role);
  const assignedPlanFeatures = new Set<string>(
      data.currentPlan
        ? (JSON.parse(String(data.currentPlan.features_json)) as string[])
        : [],
    ),
    planEntitled = ["active", "trialing"].includes(
      String(data.currentPlan?.subscription_status ?? ""),
    ),
    currentPlanFeatures = planEntitled
      ? assignedPlanFeatures
      : new Set<string>(),
    currentPlanLimits = data.currentPlan
      ? (JSON.parse(
          String(data.currentPlan.limits_json),
        ) as Record<string, number>)
      : {},
    hasPlanFeature = (feature: string) => currentPlanFeatures.has(feature),
    areaCapacityAvailable =
      currentPlanLimits.areas === undefined ||
      data.planUsage.areas < currentPlanLimits.areas,
    productCapacityAvailable =
      currentPlanLimits.permitProducts === undefined ||
      data.planUsage.permitProducts < currentPlanLimits.permitProducts,
    memberCapacityAvailable =
      currentPlanLimits.members === undefined ||
      data.planUsage.members < currentPlanLimits.members,
    canUsePermitProducts =
      data.onboarding.goals.includes("permit_issuing") ||
      (data.onboarding.goals.includes("permit_sales") &&
        hasPlanFeature("permit_sales")),
    pendingPlanChange = data.planChangeRequests.find(
      (request) => request.status === "pending",
    ),
    verifiedAreas = data.areas.filter((item) => item.status === "verified");
  const step = (id: string) =>
    data.onboarding.steps.find((item) => item.id === id);
  const isBlocked = (id: string) =>
    !step(id) || ["locked", "waiting"].includes(step(id)!.status);
  const locked = (id: string) => {
    const item = step(id),
      waiting = item?.status === "waiting";
    return (
      <div className="workspace-locked">
        <ShieldCheck />
        <div>
          <b>{item?.label ?? "Toiminto ei ole vielä käytössä"}</b>
          <p>
            {item?.blockingReason ??
              item?.action ??
              "Valitse ensin käyttötarkoitus ja suorita edeltävät vaiheet."}
          </p>
          {!waiting && (
            <button onClick={() => setTab(data.onboarding.nextAction.tab)}>
              Avaa seuraava tehtävä
            </button>
          )}
        </div>
      </div>
    );
  };
  const packageLocked = (title: string, description: string) => (
    <div className="workspace-locked package-locked">
      <PackageCheck />
      <div>
        <b>{title}</b>
        <p>{description}</p>
        <button onClick={() => setTab("package")}>
          Avaa paketti ja käyttörajat
        </button>
      </div>
    </div>
  );

  return (
    <OrganizationWorkspaceShell
      data={data}
      message={message}
      tab={tab}
      onTabChange={setTab}
    >
          {tab === "overview" && (
            <div className="workspace-content">
              <section className="workspace-next">
                <div>
                  <span className="eyebrow">Seuraava tehtävä</span>
                  <h2>{data.onboarding.nextAction.label}</h2>
                  <p>
                    Työtila avaa toiminnot järjestyksessä. Vain organisaation
                    identiteetti sekä alueiden ja lupatuotteiden määräysvalta
                    tarkistetaan erikseen.
                  </p>
                </div>
                <button onClick={() => setTab(data.onboarding.nextAction.tab)}>
                  Jatka tästä <ArrowRight />
                </button>
              </section>
              {(data.organization.status !== "active" || data.application) && (
                <OrganizationApplicationSection
                  slug={slug}
                  data={data}
                  areas={areas}
                  onSaved={(value) => finish(value)}
                />
              )}
              <div className="workspace-columns">
                <section className="account-panel">
                  <span className="eyebrow">Käyttötarkoitus</span>
                  <h2>Mitä organisaatio tekee?</h2>
                  <p className="panel-copy">
                    Valinta ottaa tarvittavat työkalut heti käyttöön. Se ei
                    lähetä erillistä hyväksyntäpyyntöä Platformiin.
                  </p>
                  {canAdmin && data.organization.status === "active" ? (
                    <form className="goal-grid" onSubmit={submitGoals}>
                      {goalDefinitions.map((goal) => (
                        <label className="goal-card" key={goal.id}>
                          <input
                            type="checkbox"
                            name="goal"
                            value={goal.id}
                            defaultChecked={data.onboarding.goals.includes(
                              goal.id,
                            )}
                          />
                          <span>
                            <b>{goal.title}</b>
                            <small>{goal.text}</small>
                          </span>
                        </label>
                      ))}
                      <button>Tallenna käyttötarkoitus</button>
                    </form>
                  ) : (
                    <div className="workspace-readonly">
                      Käyttötarkoitusta voi muuttaa organisaation hyväksymisen
                      jälkeen.
                    </div>
                  )}
                </section>
                <section className="account-panel">
                  <span className="eyebrow">Käyttöönotto</span>
                  <h2>Vaiheiden tila</h2>
                  <div className="onboarding-checklist">
                    {data.onboarding.steps.map((item, index) => (
                      <button
                        key={item.id}
                        className={item.status}
                        onClick={() => setTab(item.tab)}
                      >
                        <span>
                          {item.status === "complete" ? <Check /> : index + 1}
                        </span>
                        <div>
                          <b>{item.label}</b>
                          <small>{item.blockingReason ?? item.action}</small>
                        </div>
                        <em>{stepStatusLabel(item.status)}</em>
                      </button>
                    ))}
                  </div>
                </section>
              </div>
              <section className="account-stats workspace-stats">
                <article>
                  <MapPin />
                  <strong>{data.areas.length}</strong>
                  <span>Aluekytkentää</span>
                </article>
                <article>
                  <TicketCheck />
                  <strong>{data.permitProducts.length}</strong>
                  <span>Lupatuotekytkentää</span>
                </article>
                <article>
                  <UserRound />
                  <strong>{data.members.length}</strong>
                  <span>Tiimin jäsentä</span>
                </article>
                <article>
                  <CircleDollarSign />
                  <strong>{String(data.orderSummary.order_count ?? 0)}</strong>
                  <span>Tilausta</span>
                </article>
              </section>
            </div>
          )}

          {tab === "areas" && (
            <OrganizationAreasPage
              areas={areas}
              canEdit={canEdit}
              capacityAvailable={areaCapacityAvailable}
              capacityLocked={packageLocked(
                "Alueraja on täynnä",
                "Nykyinen palvelupaketti ei salli uusien alueiden lisäämistä.",
              )}
              data={data}
              onboardingBlocked={isBlocked("areas")}
              onboardingLocked={locked("areas")}
              onSubmit={proposeArea}
            />
          )}

          {tab === "permits" && (
            <OrganizationPermitsPage
              areas={areas}
              canEdit={canEdit}
              canUsePermitProducts={canUsePermitProducts}
              data={data}
              isBlocked={isBlocked}
              linkProduct={linkProduct}
              locked={locked}
              offer={offer}
              onSelectedProductChange={setSelectedProduct}
              packageLocked={packageLocked}
              permitSalesAvailable={hasPlanFeature("permit_sales")}
              productCapacityAvailable={productCapacityAvailable}
              products={products}
              proposePermitProduct={proposePermitProduct}
              selectedProduct={selectedProduct}
              verifiedAreas={verifiedAreas}
            />
          )}

          {tab === "team" && (
            <OrganizationTeamPage
              canAdmin={canAdmin}
              capacityAvailable={memberCapacityAvailable}
              capacityLocked={packageLocked(
                "Jäsenraja on täynnä",
                "Nykyinen palvelupaketti ei salli uusien jäsenten kutsumista.",
              )}
              data={data}
              featureAvailable={hasPlanFeature("member_management")}
              featureLocked={packageLocked(
                "Jäsenten hallinta ei kuulu pakettiin",
                "Vaihda pakettiin, joka sisältää jäsenten hallinnan.",
              )}
              onSubmit={invite}
            />
          )}

          {tab === "finance" && (
            <OrganizationFinancePage
              canAdmin={canAdmin}
              data={data}
              financialReportsAvailable={hasPlanFeature("financial_reports")}
              isBlocked={isBlocked}
              locked={locked}
              packageLocked={packageLocked}
              payment={payment}
              paymentBusy={paymentBusy}
              permitSalesAvailable={hasPlanFeature("permit_sales")}
              refundOrder={refundOrder}
            />
          )}

          {tab === "package" && (
            <OrganizationPackagePage
              assignedPlanFeatures={assignedPlanFeatures}
              canAdmin={canAdmin}
              currentPlanLimits={currentPlanLimits}
              data={data}
              openBillingCheckout={openBillingCheckout}
              openBillingPortal={openBillingPortal}
              pendingPlanChange={pendingPlanChange}
              requestPlanChange={requestPlanChange}
            />
          )}

          {tab === "settings" && (
            <OrganizationSettingsPage
              canAdmin={canAdmin}
              organization={data.organization}
              onSubmit={settings}
            />
          )}
    </OrganizationWorkspaceShell>
  );
}
